import { useState, useEffect } from 'react'
import { fmtFecha, hoyISO } from '../utils/fecha'
import { useAbm } from '../hooks/useAbm'
import AbmTable from '../components/AbmTable'
import Modal from '../components/Modal'
import Boton from '../components/Boton'
import ModalCodigosClienteArticulo from '../components/ModalCodigosClienteArticulo'
import api from '../api/client'
import { clientesApi } from '../api/notasVenta'
import { PROVINCIAS, esCapitalFederal, matchProvincia } from '../data/argentina'
import CampoLocalidad from '../components/CampoLocalidad'
import CampoDireccion from '../components/CampoDireccion'
import ModalListaVendedores from '../components/ModalListaVendedores'

const TIPOS_DOC = [
  { value: 80, label: 'CUIT' },
  { value: 85, label: 'CUIL' },
  { value: 96, label: 'DNI' },
  { value: 94, label: 'Pasaporte' },
  { value: 99, label: 'Sin identificar' },
]

const TIPOS_RESP = [
  { value: 1.0, label: 'Responsable Inscripto (RI)' },
  { value: 3.1, label: 'Monotributo' },
  { value: 4.0, label: 'Exento' },
  { value: 5.0, label: 'Consumidor Final' },
]

const EMPTY = {
  codigo_presea: '', nombre: '', tipo_doc: '', cuit: '', tipo_resp: '', tipo_factura: '',
  domicilio: '', localidad: '', provincia: '', codigo_postal: '',
  email: '', telefono: '', telefono_wa: '', lista_precios: '', activo: true,
  codigo_vendedor_presea: '', vendedor_nombre: '',
}

export default function ClientesPage() {
  const { rows, loading, create, update, remove, refresh, setPage, setSearch, meta, search, colFilters, setColFilter } = useAbm('clientes', { paginated: true })

  const [portalCopiado, setPortalCopiado] = useState(null)
  const [skusCliente, setSkusCliente] = useState(null)
  const copiarLinkPortal = async (row) => {
    try {
      const { data } = await api.get(`/clientes/${row.id}/link-portal`)
      await navigator.clipboard.writeText(data.url)
      setPortalCopiado(row.id)
      setTimeout(() => setPortalCopiado(null), 1500)
    } catch { alert('No se pudo generar el link del portal.') }
  }

  const exportar = async () => {
    const params = { ...(search ? { q: search } : {}), ...(Object.keys(colFilters).length ? { f: colFilters } : {}) }
    const res = await api.get('/clientes/export', { params, responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = 'clientes_' + hoyISO() + '.csv'
    a.click()
    URL.revokeObjectURL(url)
  }
  const [searchInput, setSearchInput] = useState('')
  const [modal, setModal] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [errores, setErrores] = useState({})
  const [showVendedores, setShowVendedores] = useState(false)
  const [afipStatus, setAfipStatus] = useState(null)
  const [afipMsg, setAfipMsg] = useState('')
  // Resultado del viaje a Presea del último guardado (ok / aviso / rechazo).
  const [preseaMsg, setPreseaMsg] = useState(null)
  const [listas, setListas] = useState([])

  useEffect(() => {
    api.get('/listas-precios').then(({ data }) => {
      const items = Array.isArray(data) ? data : (data.data || [])
      setListas(items)
    }).catch(() => setListas([]))
  }, [])

  // debounce del buscador hacia el fetch paginado
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350)
    return () => clearTimeout(t)
  }, [searchInput, setSearch])

  const openCreate = () => {
    // El código de Presea NO se pre-asigna: nace vacío ("pendiente") y Presea le
    // asigna el real de 6 dígitos en la primera factura. Se puede tipear uno manual.
    setErrores({}); setAfipStatus(null); setAfipMsg('')
    setForm(EMPTY); setModal('new')
  }
  const openEdit = (row) => {
    // El backend devuelve tipo_resp como decimal string ("5.0"); las opciones del
    // select usan el valor canónico ("5") → normalizar para que el <select> matchee
    // y la Condición IVA no aparezca vacía al reabrir (sugerencia #18).
    const tipoResp = row.tipo_resp != null && row.tipo_resp !== '' ? String(parseFloat(row.tipo_resp)) : ''
    // Un dato vacío de la ficha (null) deja el campo en blanco, no lo pisa con
    // null: un cliente sin CUIT (sin ficha en Presea) rompía la pantalla en
    // `form.cuit.replace` (4 oct 2026). Mismo criterio que ModalAltaCliente.
    const sinNulos = Object.fromEntries(Object.entries(row).map(([k, v]) => [k, v == null && k in EMPTY ? EMPTY[k] : v]))
    setForm({ ...EMPTY, ...sinNulos, tipo_resp: tipoResp, codigo_vendedor_presea: row.codigo_vendedor_presea || '', vendedor_nombre: '' }); setErrores({}); setAfipStatus(null); setAfipMsg(''); setModal(row)
  }

  // Lista negra: bloquear/desbloquear desde el modal de edición del cliente.
  // El bloqueo frena NV, remito, presupuesto, cobranza y envío a Presea (backend).
  const toggleListaNegra = async (cliente) => {
    try {
      if (cliente.lista_negra) {
        if (!window.confirm(`¿Sacar a ${cliente.nombre} de la lista negra?`)) return
        await clientesApi.desbloquear(cliente.id)
      } else {
        const motivo = window.prompt(`Bloquear a ${cliente.nombre} (no podrá operar). Motivo (opcional):`, '')
        if (motivo === null) return   // canceló
        await clientesApi.bloquear(cliente.id, motivo || null)
      }
      setModal(null)
      await refresh()
    } catch (e) {
      alert(e.response?.data?.message || 'No se pudo cambiar el estado de lista negra.')
    }
  }

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }))
  // DNI (96) ⇒ Consumidor Final por defecto (si no se eligió otra cond. IVA)
  const setTipoDoc = (e) => {
    const v = e.target.value
    setForm(f => ({ ...f, tipo_doc: v, tipo_resp: (String(v) === '96' && !f.tipo_resp) ? '5' : f.tipo_resp }))
  }
  const fieldErr = (key) => errores[key]?.[0]

  // Al editar un cliente con vendedor cargado, resolver el nombre para que el
  // campo no muestre el código pelado.
  useEffect(() => {
    if (!modal || modal === 'new') return
    const cod = form.codigo_vendedor_presea
    if (!cod || form.vendedor_nombre) return
    api.get(`/vendedores/${cod}`)
      .then(({ data }) => setForm(f => String(f.codigo_vendedor_presea) === String(cod) ? { ...f, vendedor_nombre: data.nombre || '' } : f))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal])

  // Provincia/localidad guardadas en MAYÚSCULAS sin tildes (hook saving() del
  // modelo) → matcheamos a la opción canónica para que los <select> muestren el
  // valor guardado al reabrir, sin tocar cómo se envía al backend (#24).
  const provinciaCanon = matchProvincia(form.provincia)
  const esCaba = esCapitalFederal(provinciaCanon)

  // Con qué factura sale este cliente. La letra la decide Presea a partir de la
  // condición IVA, así que fijarla a mano implica mandarle a Presea la
  // condición que corresponde: eso corrige la ficha del cliente allá, y el
  // operador tiene que saberlo antes de guardar.
  const esRi = parseFloat(form.tipo_resp) === 1
  const letraAuto = esRi ? 'A' : 'B'
  const avisoFactura = form.tipo_factura === 'B' && esRi
    ? 'Está marcado como Responsable Inscripto: al emitirle B, en Presea queda registrado como Consumidor Final.'
    : (form.tipo_factura === 'A' && !esRi
      ? 'Al emitirle A, en Presea queda registrado como Responsable Inscripto. Necesita CUIT.'
      : '')

  const validarAfip = async () => {
    const cuit = form.cuit.replace(/\D/g, '')
    if (cuit.length !== 11) { setAfipMsg('Ingresá un CUIT de 11 dígitos.'); setAfipStatus('error'); return }
    setAfipStatus('loading')
    setAfipMsg('')
    try {
      const { data } = await api.get(`/afip/contribuyente/${cuit}`)
      // ARCA dice "CIUDAD AUTONOMA BUENOS AIRES" sin localidad; el form usa
      // CAPITAL FEDERAL (y en CABA la localidad se oculta — manda el CP).
      const prov = matchProvincia(data.provincia || '')
      const caba = esCapitalFederal(prov)
      setForm(f => ({
        ...f,
        // 11 dígitos validados en el padrón = es un CUIT: el tipo de
        // documento se completa solo.
        tipo_doc: '80',
        nombre: data.nombre || f.nombre,
        // La condición fiscal la dice el padrón: define la letra de la
        // factura y las percepciones — no se deja adivinar al operador.
        tipo_resp: data.tipo_resp != null ? String(parseFloat(data.tipo_resp)) : f.tipo_resp,
        domicilio: data.domicilio || f.domicilio,
        localidad: caba ? f.localidad : (data.localidad || f.localidad),
        provincia: prov || f.provincia,
        codigo_postal: data.codigo_postal || f.codigo_postal,
      }))
      setAfipStatus('ok')
      const cond = { RI: 'Resp. Inscripto', MONOTRIBUTO: 'Monotributo', EXENTO: 'Exento', CF: 'Cons. Final' }[data.condicion_iva]
      setAfipMsg(`Validado: ${data.nombre}${cond ? ` · ${cond}` : ''}`)
    } catch (err) {
      setAfipStatus('error')
      setAfipMsg(err.response?.data?.error || 'No se pudo validar en AFIP.')
    }
  }

  // Lo que dijo Presea al guardar la ficha (el PUT replica el cambio allá).
  // La ficha de LGR ya quedó guardada en todos los casos: acá solo se informa.
  const informarPresea = (guardado) => {
    const p = guardado?.presea
    if (!p || !p.enviado) return
    if (!p.ok) {
      setPreseaMsg({ tipo: 'error', texto: `Los cambios se guardaron en LGR pero NO llegaron a Presea: ${p.error}` })
    } else if (p.aviso) {
      setPreseaMsg({ tipo: 'aviso', texto: `Cambios guardados en LGR y en Presea. ${p.aviso}` })
    } else {
      setPreseaMsg({ tipo: 'ok', texto: `Cambios de «${guardado.nombre}» guardados en LGR y en Presea (cód. ${p.codigo}).` })
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    // Provincia y lista son obligatorias en el ALTA. Al editar no: hay fichas
    // históricas sin dirección y bloquearlas impedía corregir hasta el email
    // (mismo criterio que ModalAltaCliente y que el update del backend).
    if (!form.lista_precios && modal === 'new') {
      setErrores({ lista_precios: ['Seleccioná una lista de precios para el cliente.'] })
      return
    }
    setSaving(true)
    setErrores({})
    try {
      const payload = {
        ...form,
        tipo_doc: form.tipo_doc ? parseInt(form.tipo_doc) : null,
        tipo_resp: form.tipo_resp ? parseFloat(form.tipo_resp) : null,
        // '' = automático (la letra la decide la condición IVA)
        tipo_factura: form.tipo_factura || null,
        lista_precios: form.lista_precios || null,
        localidad: esCaba ? null : (form.localidad || null),
        codigo_postal: form.codigo_postal || null,
        codigo_vendedor_presea: form.codigo_vendedor_presea ? parseInt(form.codigo_vendedor_presea) : null,
        vendedor_nombre: undefined,
      }
      if (modal === 'new') {
        await create(payload)
      } else {
        setPreseaMsg(null)
        informarPresea(await update(modal.id, payload))
      }
      setModal(null)
    } catch (error) {
      setErrores(error.response?.data?.errors || { general: [error.response?.data?.message || 'Error al guardar'] })
    } finally { setSaving(false) }
  }

  // Guarda la ficha y manda el ALTA SOLA a Presea (sin factura): Presea asigna
  // el código de 6 dígitos y registra condición fiscal + vendedor + lista.
  // Mismo circuito que el botón del alta desde la NV (proforma, 8 ago).
  const handleAltaPresea = async (e) => {
    e.preventDefault()
    if (!form.lista_precios) {
      setErrores({ lista_precios: ['Seleccioná una lista de precios para el cliente.'] })
      return
    }
    setSaving(true)
    setErrores({})
    try {
      const payload = {
        ...form,
        tipo_doc: form.tipo_doc ? parseInt(form.tipo_doc) : null,
        tipo_resp: form.tipo_resp ? parseFloat(form.tipo_resp) : null,
        tipo_factura: form.tipo_factura || null,
        lista_precios: form.lista_precios || null,
        localidad: esCaba ? null : (form.localidad || null),
        codigo_postal: form.codigo_postal || null,
        codigo_vendedor_presea: form.codigo_vendedor_presea ? parseInt(form.codigo_vendedor_presea) : null,
        vendedor_nombre: undefined,
      }
      const guardado = modal === 'new' ? await create(payload) : await update(modal.id, payload)
      try {
        const { data: alta } = await api.post(`/clientes/${guardado.id}/enviar-alta-presea`)
        window.alert(`✅ Cliente dado de alta en Presea con el código ${alta.codigo_presea}${alta.ya_existia ? ' (ya existía — se actualizó su ficha)' : ''}.`)
        refresh()
        setModal(null)
      } catch (err) {
        // La ficha LGR ya quedó guardada — avisar el rechazo y dejar el modal
        // abierto para corregir y reintentar.
        const msg = err.response?.data?.errors
          ? Object.values(err.response.data.errors).flat().join(' · ')
          : (err.response?.data?.message || 'Presea no respondió.')
        window.alert(`⚠ El cliente se guardó en LGR pero el alta en Presea falló: ${msg}`)
        refresh()
      }
    } catch (error) {
      setErrores(error.response?.data?.errors || { general: [error.response?.data?.message || 'Error al guardar'] })
    } finally { setSaving(false) }
  }

  const tipoCuit = String(form.tipo_doc) === '80'

  const mono = { fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 13 }
  const TIPO_DOC = { 80: 'CUIT', 96: 'DNI', 86: 'CUIL' }
  const COND_IVA = { 1: 'Resp. Inscripto', 4: 'Exento', 5: 'Cons. Final', 3.1: 'Monotributo', 3: 'Monotributo' }
  const columns = [
    { key: 'codigo_presea', label: 'Código', w: 100, filtrable: true, render: v => v ? <code style={mono}>{v}</code> : <span style={{ fontSize: 11, color: '#b45309', background: '#fef3c7', padding: '1px 6px', borderRadius: 4 }}>Pendiente</span> },
    { key: 'nombre', label: 'Nombre', w: 230, filtrable: true },
    { key: 'cuit', label: 'CUIT/DNI', w: 110, filtrable: true, render: v => v ? <code style={mono}>{v}</code> : '—' },
    { key: 'tipo_doc', label: 'Tipo doc', w: 80, render: v => TIPO_DOC[v] || v || '—' },
    { key: 'tipo_resp', label: 'Cond. IVA', w: 115, render: v => COND_IVA[parseFloat(v)] || v || '—' },
    { key: 'domicilio', label: 'Domicilio', w: 220, filtrable: true, render: v => v || '—' },
    { key: 'localidad', filtroMultiple: true, label: 'Localidad', w: 140, render: v => v || '—' },
    { key: 'provincia', filtroMultiple: true, label: 'Provincia', w: 120, render: v => v || '—' },
    { key: 'codigo_postal', label: 'CP', w: 65, filtrable: true, render: v => v || '—' },
    { key: 'email', label: 'Email', w: 200, filtrable: true, render: v => v || '—' },
    { key: 'telefono', label: 'Teléfono', w: 125, filtrable: true, render: v => v || '—' },
    { key: 'telefono_wa', label: 'WhatsApp', w: 125, filtrable: true, render: v => v || '—' },
    { key: 'lista_precios', filtroMultiple: true, label: 'Lista', w: 90, render: v => v || '—' },
    { key: 'vendedor_pref', label: 'Vendedor', w: 130, render: (v, row) => row.vendedor_pref?.nombre || '—' },
    { key: 'codigo_magento', label: 'Magento', w: 90, filtrable: true, render: v => v || '—' },
    { key: 'medio_contacto_pref', filtroMultiple: true, label: 'Contacto pref.', w: 110, filtrable: true, render: v => v || '—' },
    { key: 'activo', label: 'Activo', w: 65, render: v => v ? 'Sí' : 'No' },
    { key: 'sync_presea_at', label: 'Sync Presea', w: 100, render: v => v ? fmtFecha(v) : '—' },
    {
      key: '_portal', label: 'Portal', w: 70,
      render: (_, row) => (
        <Boton variante="secondary" sm title="Copiar link del portal del cliente"
          onClick={(e) => { e.stopPropagation(); copiarLinkPortal(row) }}>
          {portalCopiado === row.id ? '✓' : '🔗'}
        </Boton>
      ),
    },
    {
      key: '_skus', label: 'SKUs cliente', w: 90,
      render: (_, row) => (
        <Boton variante="secondary" sm title="Códigos del cliente para nuestros artículos"
          onClick={(e) => { e.stopPropagation(); setSkusCliente(row) }}>
          📦
        </Boton>
      ),
    },
  ]

  return (
    <div>
      {preseaMsg && (
        <div style={{ ...st.preseaMsg, ...st[`preseaMsg_${preseaMsg.tipo}`] }} role="status">
          <span>{preseaMsg.tipo === 'ok' ? '✅' : '⚠'} {preseaMsg.texto}</span>
          <button type="button" onClick={() => setPreseaMsg(null)} style={st.preseaMsgCerrar} aria-label="Cerrar">✕</button>
        </div>
      )}
      <AbmTable
        title="Clientes"
        columns={columns}
        rows={rows}
        loading={loading}
        onCreate={openCreate}
        onEdit={openEdit}
        onDelete={remove}
        pagination={meta}
        onPageChange={setPage}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Buscar por nombre, CUIT, código, domicilio, localidad, provincia o email…"
        scrollX
        columnFilters={colFilters}
        onColumnFilter={setColFilter}
        fetchFacetas={async (campo) => (await api.get('/clientes/facetas', { params: { campo, ...(search ? { q: search } : {}), ...(Object.keys(colFilters).length ? { f: colFilters } : {}) } })).data}
        onExport={exportar}
      />
      {modal && (
        <Modal title={modal === 'new' ? 'Nuevo Cliente' : 'Editar Cliente'} onClose={() => setModal(null)} maxWidth="640px">
          <form onSubmit={handleSave} style={st.form}>
            {errores.general && <div style={st.errorBox}>{errores.general[0]}</div>}

            <div>
              <label style={st.label}>Número de cliente</label>
              <input
                style={{ ...st.input, ...(modal === 'new' ? {} : { background: '#f8fafc', color: '#64748b' }) }}
                value={form.codigo_presea}
                onChange={set('codigo_presea')}
                readOnly={modal !== 'new'}
                placeholder={modal === 'new' ? 'Se asigna al facturar en Presea' : ''}
              />
              {modal === 'new' && <span style={{ ...st.err, color: '#94a3b8', fontWeight: 400 }}>Dejalo vacío: Presea le asigna el número (6 dígitos) en la primera factura. Solo cargalo si ya conocés su código de Presea.</span>}
              {fieldErr('codigo_presea') && <span style={st.err}>{fieldErr('codigo_presea')}</span>}
            </div>

            <div style={st.row2}>
              <div style={{ flex: 2 }}>
                <label style={st.label}>Nombre / Razón Social *</label>
                <input style={st.input} value={form.nombre} onChange={set('nombre')} required />
                {fieldErr('nombre') && <span style={st.err}>{fieldErr('nombre')}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <label style={st.label}>Tipo de documento</label>
                <select style={st.input} value={form.tipo_doc} onChange={setTipoDoc}>
                  <option value="">— Seleccionar —</option>
                  {TIPOS_DOC.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            <div style={st.row2}>
              <div style={{ flex: 1 }}>
                <label style={st.label}>CUIT / Documento{modal === 'new' ? ' *' : ''}</label>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <input style={{ ...st.input, flex: 1 }} value={form.cuit} onChange={e => { set('cuit')(e); setAfipStatus(null); setAfipMsg('') }} onBlur={() => { if (modal === 'new' && form.cuit.replace(/\D/g, '').length === 11 && afipStatus === null) validarAfip() }} placeholder={tipoCuit ? 'XX-XXXXXXXX-X' : ''} required={modal === 'new'} autoComplete="off" />
                  {(tipoCuit || form.cuit.replace(/\D/g, '').length === 11) && (
                    <Boton variante="secondary" sm type="button" onClick={validarAfip} disabled={afipStatus === 'loading'} title="Validar en AFIP">
                      {afipStatus === 'loading' ? '...' : 'AFIP'}
                    </Boton>
                  )}
                </div>
                {afipMsg && <span style={{ ...st.err, color: afipStatus === 'ok' ? '#166534' : '#dc2626' }}>{afipMsg}</span>}
                {afipStatus === 'ok' && <span style={st.afipBadge}>Validado AFIP</span>}
                {fieldErr('cuit') && <span style={st.err}>{fieldErr('cuit')}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <label style={st.label}>Condición IVA</label>
                <select style={st.input} value={form.tipo_resp} onChange={set('tipo_resp')}>
                  <option value="">— Seleccionar —</option>
                  {TIPOS_RESP.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            <div style={st.row2}>
              <div style={{ flex: 1 }}>
                <label style={st.label}>Factura que se le emite</label>
                <select style={st.input} value={form.tipo_factura || ''} onChange={set('tipo_factura')}>
                  <option value="">Automático (por condición IVA) — {letraAuto}</option>
                  <option value="A">Siempre factura A</option>
                  <option value="B">Siempre factura B</option>
                </select>
                {fieldErr('tipo_factura') && <span style={st.err}>{fieldErr('tipo_factura')}</span>}
                {avisoFactura && (
                  <span style={{ ...st.err, color: '#92400e' }}>{avisoFactura}</span>
                )}
              </div>
              <div style={{ flex: 1 }} />
            </div>

            <div>
              <label style={st.label}>Domicilio</label>
              <CampoDireccion
                value={form.domicilio}
                onChange={v => setForm(f => ({ ...f, domicilio: v }))}
                onPlaceSelected={d => setForm(f => ({
                  ...f,
                  // calle_completa conserva el piso/depto tipeado tras la altura.
                  domicilio: d.calle_completa || d.calle || f.domicilio,
                  localidad: d.localidad || f.localidad,
                  provincia: d.provincia || f.provincia,
                  codigo_postal: d.codigo_postal || f.codigo_postal,
                }))}
                inputStyle={st.input}
                placeholder="Calle y número"
              />
            </div>

            <div style={st.row2}>
              <div style={{ flex: 1 }}>
                <label style={st.label}>Provincia{modal === 'new' ? ' *' : ''}</label>
                <select style={st.input} value={provinciaCanon} onChange={set('provincia')} required={modal === 'new'}>
                  <option value="">— Seleccionar —</option>
                  {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              {!esCaba && (
                <div style={{ flex: 1 }}>
                  <label style={st.label}>Localidad</label>
                  <CampoLocalidad
                    provincia={provinciaCanon}
                    value={form.localidad}
                    onChange={v => setForm(f => ({ ...f, localidad: v }))}
                    inputStyle={st.input}
                    cp={form.codigo_postal}
                  />
                </div>
              )}
              <div style={{ flex: esCaba ? 1 : 0.5 }}>
                <label style={st.label}>Código postal{esCaba && modal === 'new' ? ' *' : ''}</label>
                <input style={st.input} value={form.codigo_postal} onChange={set('codigo_postal')} required={esCaba && modal === 'new'} />
              </div>
            </div>

            <div>
              <label style={st.label}>Email{modal === 'new' ? ' *' : ''}</label>
              <input style={st.input} type="email" value={form.email} onChange={set('email')} required={modal === 'new'} />
              {modal === 'new' && <span style={{ ...st.err, color: '#94a3b8', fontWeight: 400 }}>Obligatorio: sin email, Presea rechaza la factura del cliente.</span>}
              {fieldErr('email') && <span style={st.err}>{fieldErr('email')}</span>}
            </div>

            <div style={st.row2}>
              <div style={{ flex: 1 }}>
                <label style={st.label}>Teléfono{modal === 'new' ? ' *' : ''}</label>
                <input style={st.input} value={form.telefono} onChange={set('telefono')} required={modal === 'new'} autoComplete="off" />
                {fieldErr('telefono') && <span style={st.err}>{fieldErr('telefono')}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <label style={st.label}>WhatsApp</label>
                <input style={st.input} value={form.telefono_wa} onChange={set('telefono_wa')} autoComplete="off" />
              </div>
            </div>

            <div>
              <label style={st.label}>Vendedor (Presea)</label>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <input
                  style={{ ...st.input, flex: 1, background: '#f8fafc', cursor: 'pointer' }}
                  readOnly
                  value={form.codigo_vendedor_presea ? `${form.codigo_vendedor_presea}${form.vendedor_nombre ? ` — ${form.vendedor_nombre}` : ''}` : ''}
                  placeholder="Sin asignar — elegilo con la lupa"
                  onClick={() => setShowVendedores(true)}
                />
                {form.codigo_vendedor_presea && (
                  <Boton variante="ghost" sm type="button" title="Quitar vendedor"
                    onClick={() => setForm(f => ({ ...f, codigo_vendedor_presea: '', vendedor_nombre: '' }))}>✕</Boton>
                )}
                <Boton variante="secondary" sm type="button" onClick={() => setShowVendedores(true)}>🔍</Boton>
              </div>
              {fieldErr('codigo_vendedor_presea') && <span style={st.err}>{fieldErr('codigo_vendedor_presea')}</span>}
            </div>

            <div>
              <label style={st.label}>Lista de precios{modal === 'new' ? ' *' : ''}</label>
              <select style={st.input} value={form.lista_precios} onChange={set('lista_precios')} required={modal === 'new'}>
                <option value="">— Seleccionar lista —</option>
                {listas.map(l => (
                  <option key={l.id} value={l.codigo_presea || l.codigo}>
                    {l.nombre} {(l.codigo_presea || l.codigo) ? `(${l.codigo_presea || l.codigo})` : ''}
                  </option>
                ))}
              </select>
              {fieldErr('lista_precios') && <span style={st.err}>{fieldErr('lista_precios')}</span>}
            </div>

            <label style={st.checkRow}>
              <input type="checkbox" checked={form.activo} onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))} />
              Activo
            </label>

            {modal !== 'new' && (
              <div style={modal.lista_negra ? st.lnOn : st.lnOff}>
                <div>
                  {modal.lista_negra
                    ? <><strong>🚫 En lista negra.</strong> No se le puede crear NV, remito, presupuesto, cobranza ni enviar nada a Presea.{modal.lista_negra_motivo ? ` Motivo: ${modal.lista_negra_motivo}.` : ''}</>
                    : 'Este cliente opera normalmente.'}
                </div>
                <Boton variante={modal.lista_negra ? 'secondary' : 'destructive'} sm type="button" onClick={() => toggleListaNegra(modal)}>
                  {modal.lista_negra ? 'Quitar de lista negra' : '🚫 Bloquear'}
                </Boton>
              </div>
            )}

            <div style={st.actions}>
              <Boton variante="ghost" type="button" onClick={() => setModal(null)}>Cancelar</Boton>
              {/* Alta SOLA en Presea (sin factura): visible mientras el cliente
                  no tenga código asignado — Presea se lo da acá mismo. */}
              {modal === 'new' && !form.codigo_presea && (
                <Boton variante="send" type="button" disabled={saving} onClick={handleAltaPresea}
                  title="Guarda la ficha y manda el ALTA del cliente a Presea sin necesidad de facturar: Presea asigna el código y registra condición fiscal, vendedor y lista">
                  → Guardar y dar de alta en Presea
                </Boton>
              )}
              {/* Al editar, el guardado replica la ficha en Presea (abm_entidades
                  ALTA/MODIFICACION): el botón lo dice. */}
              <Boton variante="primary" type="submit" disabled={saving}
                title={modal === 'new' ? undefined : 'Guarda la ficha en LGR y manda la modificación a Presea'}>
                {saving ? 'Guardando...' : (modal === 'new' ? 'Guardar' : 'Guardar cambios en Presea')}
              </Boton>
            </div>
          </form>
          {showVendedores && (
            <ModalListaVendedores
              onSelect={(v) => { setForm(f => ({ ...f, codigo_vendedor_presea: String(v.codigo), vendedor_nombre: v.nombre || '' })) }}
              onClose={() => setShowVendedores(false)}
            />
          )}
        </Modal>
      )}
      {skusCliente && (
        <ModalCodigosClienteArticulo
          cliente={skusCliente}
          onClose={() => setSkusCliente(null)}
        />
      )}
    </div>
  )
}

const st = {
  form: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  row2: { display: 'flex', gap: '0.75rem' },
  label: { display: 'block', fontWeight: '600', fontSize: '0.8125rem', color: '#374151', marginBottom: '0.2rem' },
  input: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' },
  err: { color: '#dc2626', fontSize: '0.75rem', display: 'block', marginTop: '2px' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.875rem' },
  preseaMsg: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.875rem', marginBottom: '0.75rem', border: '1px solid transparent' },
  preseaMsg_ok: { background: '#f0fdf4', color: '#166534', borderColor: '#bbf7d0' },
  preseaMsg_aviso: { background: '#fffbeb', color: '#78350f', borderColor: '#fde68a' },
  preseaMsg_error: { background: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' },
  preseaMsgCerrar: { background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '0.875rem' },
  checkRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' },
  lnOn: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '0.6rem 0.75rem', fontSize: '0.8125rem', color: '#991b1b' },
  lnOff: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '0.6rem 0.75rem', fontSize: '0.8125rem', color: '#64748b' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.25rem' },
  afipBadge: { display: 'inline-block', marginTop: '2px', background: '#dcfce7', color: '#166534', fontSize: '0.7rem', fontWeight: '700', padding: '1px 6px', borderRadius: '10px' },
}
